import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"
import debounce from "lodash.debounce"

export default class extends Controller {
  static values = {
    options: Array,
    prompt: String,
    searchable: { type: Boolean, default: false },
    dynamic: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    maxItemCount: Number,
    requiredFormat: String,
    itemLabelSingular: { type: String, default: "item" },
    itemLabelPlural: { type: String, default: "items" },
    remoteSearchExtraQueryParamName: String,
    remoteSearchSrc: String,
    remoteSearchMethod: { type: String, default: "get" },
    remoteSearchResultsLabelKey: { type: String, default: "label" },
    remoteSearchResultsValueKey: { type: String, default: "value" },
    remoteSearchResultsDescriptionKey: String,
    remoteSearchResultsRootKey: String,
    remoteSearchQueryParam: { type: String, default: "q" }
  }

  initialize() {
    this.performRemoteSearch = debounce(this.performRemoteSearch.bind(this), 300)
  }

  connect() {
    this.selectElement = this.element.querySelector("select")
    if (!this.selectElement) return

    const isMultiple = this.selectElement.hasAttribute("multiple")

    const config = {
      allowHTML: false,
      searchEnabled: this.searchableValue,
      searchResultLimit: 10,
      placeholder: true,
      placeholderValue: this.hasPromptValue ? this.promptValue : "Select an item",
      removeItemButton: isMultiple || this.dynamicValue || this.clearableValue,
      duplicateItemsAllowed: false,
      shouldSort: false,
      noChoicesText: () => {
        const label = this.itemLabelPluralValue

        return this.searchableValue
          ? `Start typing to search for ${label}`
          : `No ${label} to choose from`
      },
      noResultsText: () => `No ${this.itemLabelPluralValue} found`,
      searchPlaceholderValue: this.searchableValue ? "Search..." : null,
      callbackOnCreateTemplates: (template) => ({
        item: ({ classNames }, data, removeItemButton) => {
          if (data.placeholder) {
            return this.getPlaceholderTemplate(template, classNames, data)
          }

          return this.getSelectedItemTemplate(template, classNames, data, removeItemButton)
        },
        choice: ({ classNames }, data) => this.getChoiceTemplate(template, classNames, data)
      })
    }

    if (this.hasMaxItemCountValue) {
      config.maxItemCount = this.maxItemCountValue

      config.maxItemText = (maxItemCount) => {
        const label = maxItemCount === 1 ? this.itemLabelSingularValue : this.itemLabelPluralValue

        return `Only ${maxItemCount} ${label} can be added`
      }
    }

    if (this.dynamicValue) {
      config.addItems = true
      config.addChoices = true
      config.editItems = true

      if (this.hasRequiredFormatValue) {
        config.addItemFilter = (value) => this.validateFormat(value)
        config.customAddItemText = this.getCustomErrorMessage()
        config.addItemText = (value) => this.getAddItemText(value)
      }
    }

    this.choices = new Choices(this.selectElement, config)

    this.selectElement.addEventListener("removeItem", (_payload) => {
      const select = this.selectElement
      const choices = select.closest(".choices")

      if (choices.getAttribute("aria-expanded") === "false") {
        select.dispatchEvent(new Event("blur"))
      }
    })

    this.selectElement.addEventListener("hideDropdown", (_payload) => {
      this.selectElement?.dispatchEvent(new Event("blur"))
    })

    if (this.hasOptionsValue && this.optionsValue.length > 0) {
      const options = this
        .optionsValue
        .map(item => this.normalizeOption(item))

      const replaceExistingSelectOptionValues = false
      this.choices.setChoices(options, "value", "label", replaceExistingSelectOptionValues)
    }

    if (this.hasRemoteSearchSrcValue) {
      this.setupRemoteSearch(this.selectElement)
    }
  }

  disconnect() {
    if (this.performRemoteSearch?.cancel) {
      this.performRemoteSearch.cancel()
    }

    if (this.choices) {
      this.choices.destroy()
      this.choices = null
    }
  }

  normalizeOption(item) {
    const customProperties = { ...item.customProperties }
    let disabled = item.disabled

    if (typeof disabled === "string" && disabled.length > 0) {
      customProperties.disabledReason = disabled
      disabled = true
    } else if (typeof disabled !== "boolean") {
      disabled = false
    }

    if (item.description) {
      customProperties.description = item.description
    }

    // { value:, label:, disabled:, selected:, customProperties: { description:, disabledReason: } }
    return { ...item, disabled, customProperties }
  }

  normalizeRemoteSearchResult(item) {
    const normalized = {
      value: item[this.remoteSearchResultsValueKeyValue],
      label: item[this.remoteSearchResultsLabelKeyValue],
      disabled: item.disabled
    }

    if (this.hasRemoteSearchResultsDescriptionKeyValue && item[this.remoteSearchResultsDescriptionKeyValue]) {
      normalized.description = item[this.remoteSearchResultsDescriptionKeyValue]
    }

    return normalized
  }

  validateFormat(value) {
    if (!value || value === "") return false

    switch (this.requiredFormatValue) {
    case "email":
      // Validation from HTML Living Standard
      // https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
      return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(value)
    case "url":
      try {
        new URL(value)
        return true
      } catch {
        return false
      }
    default:
      return true
    }
  }

  getCustomErrorMessage() {
    const messages = {
      email: "Only valid email addresses can be added",
      url: "Only valid URLs can be added"
    }

    return messages[this.requiredFormatValue] || "Only values matching specific conditions can be added"
  }

  getAddItemText(value) {
    const messages = {
      email: `Press Enter to add "<b>${value}</b>" (must be a valid email)`,
      url: `Press Enter to add "<b>${value}</b>" (must be a valid URL)`
    }

    return messages[this.requiredFormatValue] || `Press Enter to add "<b>${value}</b>"`
  }

  getPlaceholderTemplate(template, classNames, data) {
    return template(`
      <div class="${classNames.item} ${classNames.placeholder}"
           data-item
           data-id="${data.id}"
           data-value="${data.value}">
        <p class="placeholder-text">${data.label}</p>
      </div>
    `)
  }

  buildRemoveButton(classNames, data) {
    if (data.disabled) return ""

    return `
      <button type="button"
              class="${classNames.button}"
              data-button
              aria-label="Remove item: ${data.value}">
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M18 6 6 18M6 6l12 12"></path>
        </svg>
      </button>
    `
  }

  buildDataAttributes(data) {
    const attrs = [
      "data-item",
      `data-id="${data.id}"`,
      `data-value="${data.value}"`
    ]

    if (data.active) attrs.push('aria-selected="true"')
    if (data.disabled) {
      attrs.push('aria-disabled="true"')
    } else if (data.deletable !== false) {
      attrs.push("data-deletable")
    }

    return attrs.join(" ")
  }

  getSelectedItemTemplate(template, classNames, data, removeItemButton) {
    const description = this.getDescriptionFromData(data)
    const isMultiple = this.selectElement.hasAttribute("multiple")
    const button = removeItemButton ? this.buildRemoveButton(classNames, data) : ""

    if (removeItemButton && (isMultiple || this.dynamicValue)) {
      return template(`
        <span class="${classNames.item} ${classNames.itemSelectable}"
              ${this.buildDataAttributes({ ...data, deletable: !data.disabled })}
              data-select-type="multiple"
              role="option">
          <p class="item-label">${data.label}</p>${button}
        </span>
      `)
    }

    const baseClasses = `${classNames.item} ${classNames.itemSelectable}`
    const baseAttrs = this.buildDataAttributes(data)

    if (description) {
      return template(`
        <div class="${baseClasses}"
             ${baseAttrs}
             data-select-type="single">
          <section class="item-content-with-description">
            <p class="item-label-primary">${data.label}</p>
            <p class="item-description">${description}</p>
          </section>${button}
        </div>
      `)
    }

    return template(`
      <div class="${baseClasses}"
           ${baseAttrs}
           data-select-type="single">
        <p class="item-label">${data.label}</p>${button}
      </div>
    `)
  }

  getChoiceTemplate(template, classNames, data) {
    const description = this.getDescriptionFromData(data)
    const disabledReason = data.disabled && (data.customProperties?.disabledReason || data.disabledReason)
    const emptyValueClass = (!data.value || data.value.toString().trim() === "") ? "choice-empty-value" : ""

    const labelHTML = `<span class="choice-label">${data.label}</span>`
    const descriptionHTML = description ? `<span class="choice-description">${description}</span>` : ""
    const disabledReasonHTML = disabledReason ? `<span class="choice-disabled-reason">${disabledReason}</span>` : ""
    const checkmarkHTML = data.selected ? "<svg viewBox=\"0 0 24 24\" width=\"24\" height=\"24\" stroke=\"currentColor\" stroke-width=\"2\" fill=\"none\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\" class=\"choice-checkmark\"><path d=\"M20 6 9 17l-5-5\"></path></svg>" : ""

    const choiceState = data.disabled ? 'data-choice-disabled aria-disabled="true"' : "data-choice-selectable"
    const selectedState = data.selected ? 'aria-selected="true"' : 'aria-selected="false"'

    return template(`
      <div class="${classNames.item} ${classNames.itemChoice} ${emptyValueClass}"
           role="option"
           data-choice
           data-id="${data.id}"
           data-value="${data.value}"
           ${choiceState}
           ${selectedState}>
        <div>
          <div class="choice-content">${labelHTML}${descriptionHTML}</div>
          ${disabledReasonHTML}${checkmarkHTML}
        </div>
      </div>
    `)
  }

  getDescriptionFromData(data) {
    return data.customProperties?.description || data.description
  }

  getRemoteSearchExtraQueryParamValue() {
    if (!this.hasRemoteSearchExtraQueryParamNameValue) {
      return null
    }

    const name = this.selectElement.getAttribute("name").replace(/\[.*?\]/, `[${this.remoteSearchExtraQueryParamNameValue}]`)
    const form = this.selectElement.closest("form")
    const sourceElement = form.querySelector(`[name='${name}']`)
    if (sourceElement) {
      if (sourceElement.tagName == "SELECT") {
        if (sourceElement.selectedOptions[0]) {
          return sourceElement.selectedOptions[0].value
        } else {
          return null
        }
      } else { // it's a regular <input>
        return sourceElement.getAttribute("value")
      }
    } else {
      return null
    }
  }

  async performRemoteSearch(query) {
    try {
      const method = this.remoteSearchMethodValue.toLowerCase() === "post" ? "post" : "get"
      const url = new URL(this.remoteSearchSrcValue, window.location.origin)
      const fetchOptions = {
        method: method.toUpperCase(),
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      }
      const extraQueryParamValue = this.getRemoteSearchExtraQueryParamValue()

      if (method === "get") {
        url.searchParams.set(this.remoteSearchQueryParamValue, query)
        if (extraQueryParamValue) {
          url.searchParams.set(this.remoteSearchExtraQueryParamNameValue, extraQueryParamValue)
        }
      } else {
        fetchOptions.headers["Content-Type"] = "application/json"
        let queryParams = {
          [this.remoteSearchQueryParamValue]: query
        }
        if (extraQueryParamValue) {
          queryParams[this.remoteSearchExtraQueryParamNameValue] = extraQueryParamValue
        }
        fetchOptions.body = JSON.stringify(queryParams)
      }

      const response = await fetch(url.toString(), fetchOptions)
      if (!response.ok) {
        this.showSearchError()
        return
      }

      let data = await response.json()

      if (this.hasRemoteSearchResultsRootKeyValue) {
        data = data[this.remoteSearchResultsRootKeyValue] || []
      }

      if (!Array.isArray(data)) {
        this.showSearchError()
        return
      }

      const mappedData = data
        .map(item => this.normalizeRemoteSearchResult(item))
        .map(item => this.normalizeOption(item))

      if (mappedData.length > 0) {
        this.choices.setChoices(mappedData, "value", "label", true)
      }
    } catch (error) {
      this.showSearchError()
    }
  }

  setupRemoteSearch(element) {
    element.addEventListener("search", (e) => {
      const query = e.detail.value

      if (!query || query.trim() === "") {
        this.choices.clearChoices()
        return
      }

      this.performRemoteSearch(query)
    })
  }

  showSearchError() {
    this.choices.setChoices([{
      value: "",
      label: `There was a problem searching for your ${this.itemLabelPluralValue}`,
      disabled: true
    }], "value", "label", true)
  }
}
